/**
 * Data initialization helper functions
 * Handles initialization of timer data from localStorage and Redux
 */

import { secureGetItem, STORAGE_KEYS } from "./secureStorage";

/**
 * Creates data initialization handler
 * @param {Object} dependencies - Object containing all necessary dependencies
 */
export const createDataInitializationHandler = ({
  // State values
  timeTrackerLog,
  eventDetails,
  // Setters
  setTag,
  setTagId,
  setTaskName,
  setStartTime,
  setIsRunning,
  setProjectName,
  setClient,
  setClientId,
  setProjectId,
  setSprintName,
  setSprintId,
  setIsBillable,
  // Functions
  refetchGroupedTimerData,
  refetchTimerData,
}) => {
  /**
   * Initialize timer data from localStorage and Redux
   */
  const initializeData = async () => {
    try {
      const safeParse = (key) => {
        try {
          // secureGetItem already returns parsed data, no need to JSON.parse again
          return secureGetItem(key, null);
        } catch (e) {
          console.error(`Error parsing localStorage key "${key}":`, e);
          return null;
        }
      };

      // Safe data fetching from localStorage or timeTrackerLog
      const gettime = timeTrackerLog?.time || safeParse(STORAGE_KEYS.START_TIME);
      const Running = safeParse(STORAGE_KEYS.IS_RUNNING);
      const getTask = timeTrackerLog?.task || safeParse(STORAGE_KEYS.TASK);
      const getTag = timeTrackerLog?.tag || safeParse(STORAGE_KEYS.TAG);
      const getTagId = timeTrackerLog?.tagId || safeParse(STORAGE_KEYS.TAG_ID);
      const getProject = timeTrackerLog?.project || safeParse(STORAGE_KEYS.PROJECT);
      const getProjectId = timeTrackerLog?.projectId || safeParse(STORAGE_KEYS.PROJECT_ID);
      const getSprintName = timeTrackerLog?.sprintName || safeParse(STORAGE_KEYS.SPRINT_NAME);
      const getSprintId = timeTrackerLog?.sprintId || safeParse(STORAGE_KEYS.SPRINT_ID);
      const isBillable = timeTrackerLog?.isbillable || safeParse(STORAGE_KEYS.IS_BILLABLE);
      const client = timeTrackerLog?.client || safeParse(STORAGE_KEYS.CLIENT);
      const clientId = timeTrackerLog?.clientId || safeParse(STORAGE_KEYS.CLIENT_ID);
      
      // State updates
      setTag(null);
      setTagId(null);
      setTaskName(eventDetails?.title || getTask || null);

      if (gettime) setStartTime(gettime);
      if (Running !== null) setIsRunning(Running);
      setProjectName(getProject || null);
      if (getTag) setTag(getTag);
      if (getTagId) setTagId(getTagId);
      if (client) setClient(client);
      if (clientId) setClientId(clientId);
      if (getProjectId) setProjectId(getProjectId);
      setSprintName(getSprintName || "");
      setSprintId(getSprintId || "");
      if (isBillable !== null) setIsBillable(isBillable);

      // Refetch timers safely
      try {
        await refetchGroupedTimerData();
      } catch (err) {
        console.error("Error refetching grouped timer data:", err);
      }

      try {
        await refetchTimerData();
      } catch (err) {
        console.error("Error refetching timer data:", err);
      }

    } catch (err) {
      console.error("Error in initializeData:", err);
    }
  };

  /**
   * Handle Escape key press to reset state
   * @param {Event} event - Keyboard event
   * @param {Object} setters - Object containing setter functions
   * @param {Function} dispatch - Redux dispatch function
   * @param {Function} eventDetailReset - Redux action to reset event details
   * @param {Object} eventDetails - Event details object
   */
  const handleEscapeKey = (event, setters, dispatch, eventDetailReset, eventDetails) => {
    try {
      if (event.key === "Escape") {
        document.activeElement.blur();
        setters.setProjectDropdownOpen(false);
        setters.setTagDropdownOpen(false);
        setters.setCustomeTime(false);
        setters.setDropdownVisible(false);
        setters.setTaskError(false);
        setters.setSplitted(false);
        setters.setSplittedDate([]);

        if (eventDetails?.title) {
          setters.setTaskName(null);
        }
        dispatch(eventDetailReset());
      }
    } catch (err) {
      console.error("Error handling Escape key:", err);
    }
  };

  return {
    initializeData,
    handleEscapeKey,
  };
};
